/**
 * Recording Session State
 *
 * Atoms for managing the active recording session including:
 * - Recorder status (idle, recording, paused, stopped)
 * - Timer/duration tracking
 * - Start/pause/stop actions
 * - Audio streams and blob URLs
 * - OPFS session persistence
 */
import { atom } from 'jotai';

// ============================================================================
// Types
// ============================================================================

/** Valid recorder statuses (from react-media-recorder library) */
export type RecorderStatus =
  | 'idle'
  | 'recording'
  | 'stopped'
  | 'acquiring_media'
  | 'media_aborted'
  | 'paused'
  | 'permission_denied'
  | 'no_specified_media_found'
  | 'media_in_use'
  | 'invalid_media_constraints'
  | 'no_constraints'
  | 'recorder_error'
  | 'delayed_start'
  | 'stopping';

/** Pending recovery session metadata (from OPFS) */
export interface PendingRecoverySession {
  id: string;
  createdAt: number;
  unifiedAppointmentId?: string;
  standalonePatientVisitId?: string;
  patientVideaId?: string;
  organizationId?: string;
  practiceId?: string;
  templateId?: string;
  promptId?: string;
  chunkCount: number;
  lastTranscriptContext?: string;
  /** Patient name for recovery (needed for /ambient standalone notes) */
  patientFirstName?: string;
  patientLastName?: string;
  /** Ambient session ID for resuming Synapse uploads */
  ambientSessionId?: string;
}

// ============================================================================
// Base Atoms
// ============================================================================

/** Current status of the MediaRecorder */
export const recorderStatusAtom = atom<RecorderStatus>('idle');

/** Whether actively recording (not paused) */
export const isRecordingAtom = atom(false);

/** Whether recording is paused */
export const isPausedAtom = atom(false);

/** Timestamp when current recording segment started */
export const recordingStartTimeAtom = atom<number | null>(null);

/** Total accumulated time before current segment (for pause/resume) */
export const accumulatedDurationAtom = atom<number>(0);

/** Duration to display after recording is stopped */
export const stoppedRecordingDurationAtom = atom<number | null>(null);

/** Final total duration in ms - persisted for API calls after recording ends */
export const finalDurationMsAtom = atom<number | null>(null);

/** Tick counter for forcing duration recalculation */
export const tickAtom = atom<number>(0);

/** Mute segment with start/end times relative to recording start */
export interface MuteSegment {
  /** Start time in ms relative to recording start */
  startMs: number;
  /** End time in ms relative to recording start (undefined if ongoing) */
  endMs?: number;
}

/** Whether the microphone is currently silent (muted) */
export const isSilentAtom = atom<boolean>(false);

/** History of all mute segments during the recording */
export const muteSegmentsAtom = atom<MuteSegment[]>([]);

/** The recorded audio blob URL */
export const mediaBlobUrlAtom = atom<string | undefined>(undefined);

/** Uploaded audio file (when user uploads instead of recording) */
export const uploadedAudioFileAtom = atom<File | null>(null);

/** Preview audio stream for visualizations */
export const previewAudioStreamAtom = atom<MediaStream | null>(null);

/** Monitoring stream for audio level visualization */
export const monitoringStreamAtom = atom<MediaStream | null>(null);

/** Current OPFS session ID for persistence */
export const recordingSessionIdAtom = atom<string | null>(null);

/** Pending recovery session (shown in recovery modal) */
export const pendingRecoverySessionAtom = atom<PendingRecoverySession | null>(
  null,
);

/** Whether upload is in progress (for navigation blocking) */
export const isUploadInProgressAtom = atom(false);

/** Selected language for the recording (BCP 47 tag, e.g. 'en', 'es') */
export const selectedLanguageAtom = atom<string>('en-US');

/**
 * Whether audio activity has been detected on the monitoring stream.
 * Set by VoiceCard when the microphone is confirmed to be capturing audio.
 * This prevents users from starting recordings when the microphone isn't
 * actually working (e.g., hardware muted, wrong device selected).
 */
export const hasAudioActivityAtom = atom(false);

/**
 * Whether OPFS blob reconstruction is in progress for a resumed recording.
 *
 * When a crash-recovered session stops, the provider reconstructs the full
 * blob from OPFS chunks before setting mediaBlobUrlAtom. During this async
 * window, recorderStatusAtom is already 'stopped' but mediaBlobUrlAtom is
 * still undefined. Without this flag, AudioRecorder's dead-stream branch
 * would mistake reconstruction-in-progress for "no audio data" and delete
 * the OPFS session before reconstruction completes.
 */
export const isReconstructingBlobAtom = atom(false);

/** Whether a recording start was blocked because another tab is recording */
export const recordingBlockedByOtherTabAtom = atom(false);

// ============================================================================
// Derived Atoms
// ============================================================================

/** Whether a recording session is currently active (recording or paused) */
export const isRecordingSessionActiveAtom = atom((get) => {
  const status = get(recorderStatusAtom);
  return status === 'recording' || status === 'paused';
});

/** Whether the recording is currently paused */
export const isRecordingSessionPausedAtom = atom((get) => {
  const status = get(recorderStatusAtom);
  return status === 'paused';
});

/** Total elapsed recording duration (live updating via tickAtom) */
export const recordingDurationAtom = atom((get) => {
  get(tickAtom); // forces recalculation each tick
  const isRecording = get(isRecordingAtom);
  const startTime = get(recordingStartTimeAtom);
  const accumulated = get(accumulatedDurationAtom);

  if (isRecording && startTime) {
    return accumulated + Math.floor((Date.now() - startTime) / 1000);
  }

  return accumulated;
});

// ============================================================================
// Action Atoms
// ============================================================================

/** Start or resume recording */
export const startRecordingAtom = atom(null, (get, set) => {
  // Don't reset timer if already recording
  if (get(isRecordingAtom)) {
    return;
  }

  const isResuming = get(isPausedAtom);

  set(stoppedRecordingDurationAtom, null);
  set(recordingStartTimeAtom, Date.now());
  set(isRecordingAtom, true);
  set(isPausedAtom, false);
  // Reset silence tracking on new recording (but preserve segments when resuming)
  set(isSilentAtom, false);
  if (!isResuming) {
    set(muteSegmentsAtom, []);
    set(finalDurationMsAtom, null);
  }
});

/** Pause recording (preserves accumulated time) */
export const pauseRecordingAtom = atom(null, (get, set) => {
  const startTime = get(recordingStartTimeAtom);
  const accumulatedTime = get(accumulatedDurationAtom);
  const isSilent = get(isSilentAtom);
  const now = Date.now();

  // Finalize any open mute segment before pausing
  if (isSilent && startTime) {
    const segments = get(muteSegmentsAtom);
    const lastSegment = segments[segments.length - 1];
    if (lastSegment && lastSegment.endMs === undefined) {
      // endMs must be relative to total recording time (accumulated + current segment)
      set(muteSegmentsAtom, [
        ...segments.slice(0, -1),
        { ...lastSegment, endMs: accumulatedTime * 1000 + (now - startTime) },
      ]);
    }
  }

  if (startTime) {
    const elapsed = Math.floor((now - startTime) / 1000);
    set(accumulatedDurationAtom, accumulatedTime + elapsed);
  }

  set(isRecordingAtom, false);
  set(isPausedAtom, true);
  set(recordingStartTimeAtom, null);
  // Reset silence state when manually paused (keep segments for metadata)
  set(isSilentAtom, false);
});

/** Stop recording completely and clean up streams */
export const stopRecordingAtom = atom(null, (get, set) => {
  const recordingStartTime = get(recordingStartTimeAtom);
  const accumulatedTime = get(accumulatedDurationAtom);
  const isSilent = get(isSilentAtom);
  const now = Date.now();

  // Calculate and store final duration before clearing timing atoms
  // accumulatedTime is in seconds, so convert to ms before adding currentSegmentMs
  if (recordingStartTime) {
    const currentSegmentMs = now - recordingStartTime;
    set(finalDurationMsAtom, accumulatedTime * 1000 + currentSegmentMs);
  } else {
    // Stopped from paused state - just use accumulated time (converted to ms)
    set(finalDurationMsAtom, accumulatedTime * 1000);
  }

  // Finalize any open mute segment before stopping
  if (isSilent && recordingStartTime) {
    const segments = get(muteSegmentsAtom);
    const lastSegment = segments[segments.length - 1];
    if (lastSegment && lastSegment.endMs === undefined) {
      // endMs must be relative to total recording time (accumulated + current segment)
      set(muteSegmentsAtom, [
        ...segments.slice(0, -1),
        {
          ...lastSegment,
          endMs: accumulatedTime * 1000 + (now - recordingStartTime),
        },
      ]);
    }
  }

  set(isRecordingAtom, false);
  set(isPausedAtom, false);
  set(recordingStartTimeAtom, null);
  set(accumulatedDurationAtom, 0);
  set(stoppedRecordingDurationAtom, null);
  // Reset silence state (but keep muteSegmentsAtom for metadata retrieval)
  set(isSilentAtom, false);

  // Get current streams before clearing
  const previewStream = get(previewAudioStreamAtom);
  const monitoringStream = get(monitoringStreamAtom);

  // Stop all tracks in preview stream
  if (previewStream) {
    previewStream.getTracks().forEach((track) => track.stop());
  }

  // Stop all tracks in monitoring stream
  if (monitoringStream) {
    monitoringStream.getTracks().forEach((track) => track.stop());
  }

  set(previewAudioStreamAtom, null);
  set(monitoringStreamAtom, null);
});

/** Enter silence state (adds new open segment to muteSegmentsAtom) */
export const enterSilenceAtom = atom(null, (get, set) => {
  // Only track silence while recording (not paused)
  if (!get(isRecordingAtom)) return;
  if (get(isSilentAtom)) return; // Already silent

  const recordingStartTime = get(recordingStartTimeAtom);
  if (!recordingStartTime) return;

  const now = Date.now();
  const segments = get(muteSegmentsAtom);
  const accumulatedTime = get(accumulatedDurationAtom);

  // startMs must be relative to total recording time (accumulated + current segment)
  const startMs = accumulatedTime * 1000 + (now - recordingStartTime);

  set(isSilentAtom, true);
  set(muteSegmentsAtom, [...segments, { startMs }]);
});

/** Threshold in seconds for early vs late silence (10 minutes). */
const EARLY_SILENCE_THRESHOLD_SECONDS = 600;

/** Silence severity based on recording duration: 'none' | 'early' (<10min) | 'late' (>=10min) */
export const silenceSeverityAtom = atom<'none' | 'early' | 'late'>((get) => {
  if (!get(isSilentAtom)) return 'none';
  if (!get(isRecordingSessionActiveAtom)) return 'none';
  const duration = get(recordingDurationAtom);
  return duration < EARLY_SILENCE_THRESHOLD_SECONDS ? 'early' : 'late';
});

/** Exit silence state (closes the last open segment in muteSegmentsAtom) */
export const exitSilenceAtom = atom(null, (get, set) => {
  if (!get(isSilentAtom)) return; // Not currently silent

  const recordingStartTime = get(recordingStartTimeAtom);
  if (!recordingStartTime) return;

  const now = Date.now();
  const segments = get(muteSegmentsAtom);
  const accumulatedTime = get(accumulatedDurationAtom);
  const lastSegment = segments[segments.length - 1];

  // Close the last segment if it's open
  // endMs must be relative to total recording time (accumulated + current segment)
  if (lastSegment && lastSegment.endMs === undefined) {
    set(muteSegmentsAtom, [
      ...segments.slice(0, -1),
      {
        ...lastSegment,
        endMs: accumulatedTime * 1000 + (now - recordingStartTime),
      },
    ]);
  }

  set(isSilentAtom, false);
});
